import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { LayoutTemplate, RefreshCw } from 'lucide-react';
import { useLocation, useNavigate } from 'react-router-dom';
import { fetchCategoriesApi } from '../../api/categories.api';
import { cloneProjectApi } from '../../api/projects.api';
import { fetchPublicProjectByIdApi, fetchPublicProjectsApi } from '../../api/publicProjects.api';
import { Button } from '../../components/ui/Button';
import {
  buildPublicTemplateCategoryFilters,
  getLandingPublicTemplatesPreviewCount,
  PUBLIC_TEMPLATE_CATEGORY_ALL
} from '../../constants/publicTemplateCategories';
import { useAppSelector } from '../../hooks/useAppRedux';
import { useI18n } from '../../hooks/useI18n';
import {
  clearPendingTemplateClone,
  getPendingTemplateClone,
  savePendingTemplateClone
} from '../../utils/storage';
import { classNames } from '../../utils/classNames';
import { PublicTemplateCard } from './PublicTemplateCard';
import { PublicTemplateDetailModal } from './PublicTemplateDetailModal';
import { PublicTemplateEmptyState } from './PublicTemplateEmptyState';
import { PublicTemplateSkeleton } from './PublicTemplateSkeleton';

export const PublicTemplatesSection = ({ variant = 'dashboard', onCreateProject } = {}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const isAuthenticated = useAppSelector((state) => state.auth.isAuthenticated);
  const { t } = useI18n();
  const isLanding = variant === 'landing';
  const isPage = variant === 'page';
  const [projects, setProjects] = useState([]);
  const [categories, setCategories] = useState([]);
  const [activeCategoryId, setActiveCategoryId] = useState(PUBLIC_TEMPLATE_CATEGORY_ALL);
  const [showAllTemplates, setShowAllTemplates] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedProject, setSelectedProject] = useState(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const [cloning, setCloning] = useState(false);
  const [cloneError, setCloneError] = useState('');
  const pendingTemplateHandledRef = useRef(false);
  const projectsRequestIdRef = useRef(0);
  const [previewCount, setPreviewCount] = useState(() => getLandingPublicTemplatesPreviewCount());

  useEffect(() => {
    if (!isLanding) {
      return undefined;
    }

    const updatePreviewCount = () => {
      setPreviewCount(getLandingPublicTemplatesPreviewCount());
    };

    updatePreviewCount();
    window.addEventListener('resize', updatePreviewCount);

    return () => {
      window.removeEventListener('resize', updatePreviewCount);
    };
  }, [isLanding]);

  useEffect(() => {
    let cancelled = false;

    const loadCategories = async () => {
      try {
        const items = await fetchCategoriesApi();
        if (!cancelled) {
          setCategories(items);
        }
      } catch {
        if (!cancelled) {
          setCategories([]);
        }
      }
    };

    loadCategories();

    return () => {
      cancelled = true;
    };
  }, []);

  const loadProjects = useCallback(
    async (categoryId = activeCategoryId) => {
      const requestId = ++projectsRequestIdRef.current;
      setLoading(true);
      setError('');

      try {
        const items = await fetchPublicProjectsApi({
          categoryId: isPage ? PUBLIC_TEMPLATE_CATEGORY_ALL : categoryId
        });
        if (requestId !== projectsRequestIdRef.current) {
          return;
        }
        setProjects(items);
      } catch (requestError) {
        if (requestId !== projectsRequestIdRef.current) {
          return;
        }
        setProjects([]);
        setError(requestError.message || t('publicTemplatesLoadError'));
      } finally {
        if (requestId === projectsRequestIdRef.current) {
          setLoading(false);
        }
      }
    },
    [isPage, t]
  );

  const catalogQueryId = isPage ? PUBLIC_TEMPLATE_CATEGORY_ALL : activeCategoryId;

  useEffect(() => {
    loadProjects(catalogQueryId);
  }, [catalogQueryId, loadProjects]);

  const visibleProjects = useMemo(() => {
    const catalog =
      isPage && activeCategoryId !== PUBLIC_TEMPLATE_CATEGORY_ALL
        ? projects.filter((project) =>
            project.categories.some((category) => category.id === activeCategoryId)
          )
        : projects;

    if (!isLanding || showAllTemplates) {
      return catalog;
    }

    return catalog.slice(0, previewCount);
  }, [activeCategoryId, isLanding, isPage, previewCount, projects, showAllTemplates]);

  const hasMoreTemplates = isLanding && !showAllTemplates && projects.length > previewCount;

  const openTemplateModal = useCallback(
    async (templateId) => {
      if (!templateId) {
        return;
      }

      const existing = projects.find((entry) => entry.id === templateId);

      if (existing) {
        setSelectedProject(existing);
        setDetailOpen(true);
        setCloneError('');
        return;
      }

      try {
        const details = await fetchPublicProjectByIdApi(templateId);
        setSelectedProject(details);
        setDetailOpen(true);
        setCloneError('');
      } catch (requestError) {
        setError(requestError.message || t('publicTemplatesDetailError'));
      }
    },
    [projects, t]
  );

  useEffect(() => {
    if (!isLanding || loading || pendingTemplateHandledRef.current) {
      return;
    }

    const templateId =
      location.state?.openPublicTemplateId || getPendingTemplateClone();

    if (!templateId) {
      return;
    }

    pendingTemplateHandledRef.current = true;

    openTemplateModal(templateId).finally(() => {
      clearPendingTemplateClone();

      if (location.state?.openPublicTemplateId) {
        navigate('/', { replace: true, state: {} });
      }

      window.setTimeout(() => {
        document.getElementById('public-templates-title')?.scrollIntoView({
          behavior: 'smooth',
          block: 'start'
        });
      }, 120);
    });
  }, [isLanding, loading, location.state, navigate, openTemplateModal]);

  const goToCreateProject = (projectDraft) => {
    const navigationState = {
      openCreateProject: true,
      ...(projectDraft ? { projectDraft } : {})
    };

    if (isAuthenticated) {
      navigate('/projects', { state: navigationState });
      return;
    }

    navigate('/login', {
      state: {
        redirectTo: '/projects',
        ...navigationState
      }
    });
  };

  const openCreateProject = () => {
    if (onCreateProject) {
      onCreateProject();
      return;
    }

    if (isLanding) {
      document.getElementById('landing-prompt')?.focus({ preventScroll: false });
      document.getElementById('landing-prompt')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return;
    }

    goToCreateProject();
  };

  const handleUseTemplate = async (project) => {
    if (!isLanding && !isPage) {
      setDetailOpen(false);
      setSelectedProject(null);
      goToCreateProject({
        name: project.title,
        description: project.description,
        visibility: 'your'
      });
      return;
    }

    if (!isAuthenticated) {
      savePendingTemplateClone(project.id);
      setDetailOpen(false);
      setSelectedProject(null);

      navigate('/login', {
        state: {
          redirectTo: '/',
          openPublicTemplateId: project.id
        }
      });
      return;
    }

    setCloning(true);
    setCloneError('');

    try {
      const clonedProject = await cloneProjectApi(project.id);
      clearPendingTemplateClone();
      setDetailOpen(false);
      setSelectedProject(null);
      navigate(`/workspace/${clonedProject.id}`, { state: { isNewProject: true } });
    } catch (requestError) {
      setCloneError(requestError.message || t('publicTemplatesCloneError'));
    } finally {
      setCloning(false);
    }
  };

  const handleCloseModal = () => {
    if (cloning) {
      return;
    }

    setDetailOpen(false);
    setSelectedProject(null);
    setCloneError('');
  };

  const handleCategoryChange = (categoryId) => {
    if (!isPage) {
      projectsRequestIdRef.current += 1;
      setLoading(true);
    }

    setActiveCategoryId(categoryId);
    setShowAllTemplates(false);
  };

  const categoryFilters = useMemo(
    () => buildPublicTemplateCategoryFilters(categories, t),
    [categories, t]
  );

  const cardVariant = isPage ? 'catalog' : isLanding ? 'minimal' : 'default';
  const gridClassName = isLanding
    ? 'grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3'
    : 'grid gap-3 sm:grid-cols-2 xl:grid-cols-3';

  const categoryChipClass = (isActive) =>
    classNames(
      'shrink-0 rounded-full px-3 py-1.5 text-xs font-semibold transition',
      isActive
        ? 'bg-accent text-white dark:text-slate-950'
        : 'border border-border/80 text-muted hover:border-accent/30 hover:text-text'
    );

  const categoryFilterRow = categoryFilters.length > 0 ? (
    <div className="scrollbar-thin flex gap-2 overflow-x-auto pb-0.5">
      {categoryFilters.map((category) => (
        <button
          key={category.id}
          type="button"
          onClick={() => handleCategoryChange(category.id)}
          className={categoryChipClass(activeCategoryId === category.id)}
        >
          {category.name}
        </button>
      ))}
    </div>
  ) : null;

  const templatesBody = (
    <>
      {error ? (
        <div
          className={classNames(
            'rounded-xl border p-4 text-sm',
            isPage
              ? 'border-rose-200 bg-rose-50 text-rose-700 dark:border-rose-900/40 dark:bg-rose-900/20 dark:text-rose-200'
              : 'border-rose-200 bg-rose-50/80 text-rose-700 dark:border-rose-900/40 dark:bg-rose-900/20 dark:text-rose-200',
            !isPage && 'mt-4'
          )}
        >
          <p>{error}</p>
          <Button variant="secondary" className="mt-3" onClick={() => loadProjects(activeCategoryId)}>
            <RefreshCw size={16} />
            {t('publicTemplatesRetry')}
          </Button>
        </div>
      ) : null}

      <div className={classNames(!isPage && 'mt-5')}>
        {loading ? (
          <div className={gridClassName}>
            <PublicTemplateSkeleton
              count={isLanding ? previewCount : 6}
              variant={cardVariant}
            />
          </div>
        ) : visibleProjects.length > 0 ? (
          <>
            <div className={gridClassName}>
              {visibleProjects.map((project) => (
                <PublicTemplateCard
                  key={project.id}
                  project={project}
                  variant={cardVariant}
                  onClick={(entry) => {
                    setCloneError('');
                    setSelectedProject(entry);
                    setDetailOpen(true);
                  }}
                />
              ))}
            </div>

            {hasMoreTemplates ? (
              <div className="mt-4 flex justify-center">
                <button
                  type="button"
                  onClick={() => setShowAllTemplates(true)}
                  className="rounded-full border border-border px-4 py-2 text-sm font-medium text-muted transition hover:border-accent/40 hover:text-text"
                >
                  {t('publicTemplatesShowAll')}
                </button>
              </div>
            ) : null}
          </>
        ) : !error ? (
          <PublicTemplateEmptyState
            onCreateProject={openCreateProject}
            actionLabel={isLanding || isPage ? t('publicTemplatesEmptyActionLanding') : undefined}
            compact={isLanding || isPage}
          />
        ) : null}
      </div>
    </>
  );

  return (
    <>
      {isPage ? (
        <div className="space-y-4">
          {categoryFilterRow}
          {templatesBody}
        </div>
      ) : (
        <section
          className={classNames(
            isLanding
              ? 'mt-6 rounded-2xl border border-border bg-panel/90 p-5 shadow-sm backdrop-blur-sm sm:mt-8 md:p-6'
              : 'glass-panel mt-4 rounded-[1.5rem] border border-border p-5 md:p-6'
          )}
          aria-labelledby="public-templates-title"
        >
          {isLanding ? (
            <div className="space-y-4">
              <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                <h2 id="public-templates-title" className="text-base font-semibold text-text sm:text-lg">
                  {t('publicTemplatesTitle')}
                </h2>
                {categoryFilterRow}
              </div>
            </div>
          ) : (
            <>
              <div className="flex flex-col gap-4 border-b border-border/70 pb-5 sm:flex-row sm:items-end sm:justify-between">
                <div className="max-w-2xl">
                  <div className="inline-flex items-center gap-2 rounded-full border border-border bg-white/50 px-3 py-1 text-xs font-semibold text-muted dark:bg-slate-950/30">
                    <LayoutTemplate size={14} className="text-accent" />
                    {t('publicTemplatesEyebrow')}
                  </div>
                  <h2 id="public-templates-title" className="mt-3 text-lg font-bold text-text md:text-xl">
                    {t('publicTemplatesTitle')}
                  </h2>
                  <p className="mt-1 text-sm leading-7 text-muted">{t('publicTemplatesBody')}</p>
                </div>

                {!loading && projects.length > 0 ? (
                  <Button
                    variant="secondary"
                    className="w-full sm:w-auto"
                    onClick={() => loadProjects(activeCategoryId)}
                  >
                    <RefreshCw size={16} />
                    {t('publicTemplatesRefresh')}
                  </Button>
                ) : null}
              </div>

              <div className="mt-3">{categoryFilterRow}</div>
            </>
          )}

          {templatesBody}
        </section>
      )}

      <PublicTemplateDetailModal
        open={detailOpen}
        projectId={selectedProject?.id}
        fallbackProject={selectedProject}
        actionLoading={cloning}
        actionError={cloneError}
        onClose={handleCloseModal}
        onUseTemplate={handleUseTemplate}
      />
    </>
  );
};
